import React, { Component } from "react";
import HelperClass from "../../config/helperClass";
import {
  recordResellerPayment,
  getResellerClientCreditBalance,
  getInvoicePayments,
  getInvoiceFinancialSummary,
} from "../../config/reseller_api_calls";
import { addClientCredit, addInvoicePaymentDebit, getAccountData } from "../../config/api_calls";
import NotificationToast from "../Notification/NotificationToast";
import { toDateInputValue } from "../../utils/helpers";

const auth = new HelperClass();

function fmtCurrency(val, decimals) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "$0.00";
  const fixed = n.toFixed(decimals || 2);
  const [intPart, decPart] = fixed.split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return "$" + (decPart !== undefined ? withCommas + "." + decPart : withCommas);
}

function fmtDate(val) {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}-${dd}-${d.getFullYear()}`;
  } catch {
    return val;
  }
}

function todayStr() {
  // Local, not UTC — toISOString() stamps yesterday's date late in the evening
  // east of Greenwich.
  return toDateInputValue(new Date());
}

function initStateFromInvoice(invoice) {
  const grandTotal    = Number(invoice.grand_total)    || 0;
  const creditApplied = Number(invoice.credit_applied) || 0;
  const alreadyPaid   = Number(invoice.amount_paid)    || 0;
  const totalSettled  = alreadyPaid + creditApplied;
  const balanceDue    = Math.max(0, grandTotal - totalSettled);
  return {
    payAmount:          String(balanceDue.toFixed(2)),
    payDate:            todayStr(),
    payMethod:          "Wire Transfer",
    payReference:       "",
    paySubmitting:      false,
    paySucceeded:       false,
    payError:           "",
    paySuccess:         "",
    toastMsg:           "",
    toastType:          "success",
    payBalanceDue:      balanceDue,
    payAlreadyPaid:     totalSettled,
    payApplyCredit:     false,
    payCreditAmount:    "0",
    payAvailableCredit: 0,
    payCreditLoading:   true,
    payHistory:         [],
    payHistoryLoading:  true,
    recordedByName:     "",
  };
}

class RecordPaymentModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      payAmount:          "",
      payDate:            todayStr(),
      payMethod:          "Wire Transfer",
      payReference:       "",
      paySubmitting:      false,
      paySucceeded:       false,
      payError:           "",
      paySuccess:         "",
      toastMsg:           "",
      toastType:          "success",
      payBalanceDue:      0,
      payAlreadyPaid:     0,
      payApplyCredit:     false,
      payCreditAmount:    "0",
      payAvailableCredit: 0,
      payCreditLoading:   false,
      payHistory:         [],
      payHistoryLoading:  false,
      recordedByName:     "",
    };
  }

  componentDidUpdate(prevProps) {
    const { show, invoice } = this.props;
    if (!prevProps.show && show && invoice) {
      this.setState(initStateFromInvoice(invoice), () => {
        this.fetchFinancials(invoice);
        this.fetchPaymentHistory(invoice.invoice_no);
        this.fetchRecordedByName();
      });
    }
  }

  fetchFinancials = async (invoice) => {
    try {
      const resp = await getInvoiceFinancialSummary(
        auth.getAccount(), auth.getToken(), invoice.invoice_no
      );
      const payload = resp && resp.data;
      if (payload && payload.status === 200 && payload.data) {
        const d = payload.data;
        const totalSettled = Number(d.amount_paid) || 0;
        const balanceDue   = Number(d.balance_due) || 0;
        this.setState({
          payBalanceDue:  balanceDue,
          payAlreadyPaid: totalSettled,
          payAmount:      String(balanceDue.toFixed(2)),
        });
      }
    } catch (e) {
      console.log("fetchFinancials error:", e);
    }

    if (invoice.client_accountno) {
      this.fetchCreditBalance(invoice.client_accountno);
    } else {
      this.setState({ payCreditLoading: false });
    }
  };

  fetchPaymentHistory = async (invoiceNo) => {
    this.setState({ payHistoryLoading: true });
    try {
      const response = await getInvoicePayments(auth.getAccount(), auth.getToken(), invoiceNo);
      const payload = response && response.data;
      if (payload && payload.status === 200 && payload.data) {
        this.setState({ payHistory: payload.data.payments || [], payHistoryLoading: false });
      } else {
        this.setState({ payHistory: [], payHistoryLoading: false });
      }
    } catch {
      this.setState({ payHistory: [], payHistoryLoading: false });
    }
  };

  fetchCreditBalance = async (clientAccountno) => {
    this.setState({ payCreditLoading: true });
    try {
      const response = await getResellerClientCreditBalance(
        auth.getAccount(), auth.getToken(), clientAccountno
      );
      const payload = response && response.data;
      if (payload && payload.status === 200 && payload.data && payload.data.summary) {
        this.setState({ payAvailableCredit: Number(payload.data.summary.balance) || 0, payCreditLoading: false });
      } else {
        this.setState({ payCreditLoading: false });
      }
    } catch {
      this.setState({ payCreditLoading: false });
    }
  };

  fetchRecordedByName = async () => {
    try {
      const response = await getAccountData(
        auth.getAccount(), auth.getToken(),
        auth.getSubUserAccount(), auth.getUserType()
      );
      const data = response && response.data;
      if (data && data.status === 200 && data.data && data.data[0]) {
        this.setState({ recordedByName: data.data[0].name || "" });
      }
    } catch (e) {}
  };

  handleClose = () => {
    if (this.state.paySubmitting) return;
    const { onClose } = this.props;
    if (onClose) onClose();
  };

  // Returns the effective credit amount: user-entered value capped at min(available, balance)
  getEffectiveCredit = () => {
    const { payApplyCredit, payCreditAmount, payAvailableCredit, payBalanceDue } = this.state;
    if (!payApplyCredit) return 0;
    const maxCredit = Math.min(payAvailableCredit, payBalanceDue);
    return Math.min(Math.max(0, Number(payCreditAmount) || 0), maxCredit);
  };

  onPayApplyCreditToggle = (checked) => {
    const { payBalanceDue, payAvailableCredit } = this.state;
    if (checked) {
      const maxCredit   = Math.min(payAvailableCredit, payBalanceDue);
      const cashPortion = Math.max(0, payBalanceDue - maxCredit);
      this.setState({
        payApplyCredit:  true,
        payCreditAmount: String(maxCredit.toFixed(2)),
        payAmount:       String(cashPortion.toFixed(2)),
      });
    } else {
      this.setState({
        payApplyCredit:  false,
        payCreditAmount: "0",
        payAmount:       String(payBalanceDue.toFixed(2)),
      });
    }
  };

  onCreditAmountChange = (rawValue) => {
    const { payBalanceDue, payAvailableCredit } = this.state;
    const maxCredit  = Math.min(payAvailableCredit, payBalanceDue);
    const parsed     = Number(rawValue);
    // Store the raw string so the input field is freely editable; cap only on blur/submit
    const creditVal  = Number.isFinite(parsed) ? Math.min(Math.max(0, parsed), maxCredit) : 0;
    const cashPortion = Math.max(0, payBalanceDue - creditVal);
    this.setState({
      payCreditAmount: rawValue,
      payAmount:       String(cashPortion.toFixed(2)),
    });
  };

  submitPayment = async () => {
    const { invoice, onSuccess } = this.props;
    const {
      payAmount, payDate, payMethod, payReference,
      payBalanceDue,
    } = this.state;

    const d             = this.props.decimals || 2;
    const cashAmount    = Number(payAmount) || 0;
    const creditToApply = this.getEffectiveCredit();
    const totalSettled  = cashAmount + creditToApply;
    const autoMarkAs    = payBalanceDue > 0 && totalSettled >= payBalanceDue ? "full" : "partial";

    if (cashAmount < 0) {
      this.setState({ payError: "Payment amount cannot be negative.", toastMsg: "Payment amount cannot be negative.", toastType: "error" }); return;
    }
    if (!payDate) {
      this.setState({ payError: "Payment date is required.", toastMsg: "Payment date is required.", toastType: "error" }); return;
    }
    if (!payMethod) {
      this.setState({ payError: "Payment method is required.", toastMsg: "Payment method is required.", toastType: "error" }); return;
    }
    if (cashAmount === 0 && creditToApply === 0) {
      this.setState({ payError: "Enter a payment amount or apply a credit.", toastMsg: "Enter a payment amount or apply a credit.", toastType: "error" }); return;
    }
    if (totalSettled > payBalanceDue + 0.001) {
      const msg = `Total settlement ${fmtCurrency(totalSettled, d)} exceeds balance due ${fmtCurrency(payBalanceDue, d)}.`;
      this.setState({ payError: msg, toastMsg: msg, toastType: "error" }); return;
    }

    this.setState({ paySubmitting: true, payError: "", paySuccess: "" });
    try {
      const response = await recordResellerPayment(
        auth.getAccount(), auth.getToken(),
        invoice.invoice_no,
        cashAmount, payDate, payMethod, payReference, "",
        creditToApply > 0, creditToApply, autoMarkAs,
        invoice.client_accountno || "",
        this.state.recordedByName || auth.getAdminFullName() || auth.getAdminName() || ""
      );
      const payload = response && response.data;
      if (payload && (payload.status === 403 || payload.errors === "authentication missing")) {
        auth.logout(); return;
      }
      if (payload && payload.status === 200) {
        const newStatus = (payload.data && payload.data.payment_status) || "Paid";
        const successMsg = `Payment recorded — invoice marked as ${newStatus}.`;
        this.setState({ paySubmitting: false, paySucceeded: true, paySuccess: successMsg, toastMsg: successMsg, toastType: "success" });

        // Post credit first, then debit — await ensures credit is always inserted before debit
        const clientAccountno = invoice.client_accountno || "";
        if (clientAccountno && cashAmount > 0) {
          const parts = [];
          if (invoice.invoice_no) parts.push(`${invoice.invoice_no} payment`);
          if (payMethod) parts.push(`via ${payMethod}`);
          if (payReference) parts.push(payReference);
          if (payDate) parts.push(`Date: ${payDate}`);
          const entryDesc = parts.join(" | ") || "Invoice payment received";

          await addClientCredit(
            auth.getAccount(), auth.getToken(),
            clientAccountno, cashAmount, entryDesc,
            auth.getName() || auth.getAccount()
          ).catch((err) => console.error("addClientCredit failed:", err));

          addInvoicePaymentDebit(
            auth.getAccount(), auth.getToken(),
            clientAccountno, cashAmount, entryDesc,
            invoice.invoice_no || "",
            auth.getName() || auth.getAccount()
          ).catch((err) => console.error("addInvoicePaymentDebit failed:", err));
        }

        setTimeout(() => { if (onSuccess) onSuccess(); }, 1800);
      } else {
        const msg = (payload && payload.message) || "Failed to record payment.";
        this.setState({ paySubmitting: false, payError: msg, toastMsg: msg, toastType: "error" });
      }
    } catch {
      this.setState({ paySubmitting: false, payError: "Failed to record payment.", toastMsg: "Failed to record payment.", toastType: "error" });
    }
  };

  render() {
    const { show, invoice, decimals } = this.props;
    if (!show || !invoice) return null;

    const d = decimals || 2;
    const {
      payAmount, payDate, payMethod, payReference,
      paySubmitting, paySucceeded, payError, paySuccess,
      payBalanceDue, payAlreadyPaid, payApplyCredit, payAvailableCredit,
      payCreditAmount, payCreditLoading, payHistory, payHistoryLoading,
    } = this.state;

    const grandTotal    = Number(invoice.grand_total) || 0;
    const creditToApply = this.getEffectiveCredit();
    const cashPortion   = Number(payAmount) || 0;
    const totalSettled  = cashPortion + creditToApply;
    const autoMarkAs    = payBalanceDue > 0 && totalSettled >= payBalanceDue ? "full" : "partial";
    const maxCredit     = Math.min(payAvailableCredit, payBalanceDue);
    const exceedsBalance = totalSettled > payBalanceDue + 0.001;
    const dataLoading   = payCreditLoading || payHistoryLoading;

    return (
      <>
      <div
        className="modal fade show"
        style={{ display: "block", background: "rgba(0,0,0,0.45)", zIndex: 1050 }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "540px" }}>
          <div className="pay-modal-card">

            {/* Header */}
            <div className="pay-modal-header">
              <div>
                <div className="pay-modal-title">Record Payment</div>
                <div className="pay-modal-subtitle">
                  {invoice.invoice_no} · {invoice.customer_name || "Client"} · Balance Due: {fmtCurrency(payBalanceDue, d)}
                </div>
              </div>
              <button className="pay-modal-close" onClick={this.handleClose} disabled={paySubmitting}>×</button>
            </div>

            <div className="pay-modal-body" style={{ position: "relative" }}>
              {dataLoading && (
                <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, borderRadius: 4 }}>
                  <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} />
                  <span style={{ fontSize: 13, color: "#6B6762" }}>Loading…</span>
                </div>
              )}

              {/* Invoice summary strip */}
              <div className="pay-summary-strip">
                <div className="pay-summary-col">
                  <div className="pay-summary-label">INVOICE TOTAL</div>
                  <div className="pay-summary-val">{fmtCurrency(grandTotal, d)}</div>
                </div>
                <div className="pay-summary-col">
                  <div className="pay-summary-label">ALREADY PAID</div>
                  <div className="pay-summary-val pay-summary-green">{fmtCurrency(payAlreadyPaid, d)}</div>
                </div>
                <div className="pay-summary-col">
                  <div className="pay-summary-label">BALANCE DUE</div>
                  <div className="pay-summary-val pay-summary-orange">{fmtCurrency(payBalanceDue, d)}</div>
                </div>
                {invoice.billing_period && (
                  <div style={{ gridColumn: "1/-1", marginTop: 8, fontSize: 12, color: "#6B6762" }}>
                    Billing period: <strong>{invoice.billing_period}</strong>
                  </div>
                )}
              </div>

              {/* Apply credit balance */}
              <div className={`pay-credit-box${payApplyCredit ? " active" : ""}${!payCreditLoading && payAvailableCredit === 0 ? " no-credit" : ""}`}>
                <label className="pay-credit-row">
                  <input
                    type="checkbox"
                    className="pay-credit-check"
                    checked={payApplyCredit}
                    onChange={(e) => this.onPayApplyCreditToggle(e.target.checked)}
                    disabled={paySubmitting || payCreditLoading || payAvailableCredit === 0}
                  />
                  <em
                    className="icon ni ni-cc-alt"
                    style={{ color: payCreditLoading || payAvailableCredit > 0 ? "#16a34a" : "#9A9690", fontSize: 16, marginRight: 4 }}
                  ></em>
                  <span className="pay-credit-label" style={{ color: !payCreditLoading && payAvailableCredit === 0 ? "#9A9690" : undefined }}>
                    Apply credit balance{" "}
                    <strong>
                      {payCreditLoading
                        ? "checking…"
                        : payAvailableCredit > 0
                          ? fmtCurrency(payAvailableCredit, d) + " available"
                          : "No credit available"}
                    </strong>
                  </span>
                </label>
                {payApplyCredit && payAvailableCredit > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <label className="pay-field-label" style={{ fontSize: 12, marginBottom: 4, display: "block" }}>
                      Credit to Apply (USD) — up to {fmtCurrency(maxCredit, d)}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={maxCredit}
                      step="0.01"
                      className="pay-field-input"
                      style={{ maxWidth: 180 }}
                      value={payCreditAmount}
                      onChange={(e) => this.onCreditAmountChange(e.target.value)}
                      disabled={paySubmitting}
                    />
                    <div className="pay-credit-desc" style={{ marginTop: 4 }}>
                      Applies <strong>{fmtCurrency(creditToApply, d)}</strong> from this client's credit balance toward this invoice. The rest is collected via the payment method below.
                    </div>
                  </div>
                )}
                {!payApplyCredit && payAvailableCredit > 0 && (
                  <div className="pay-credit-desc">
                    Up to <strong>{fmtCurrency(maxCredit, d)}</strong> can be applied from this client's credit balance.
                  </div>
                )}
              </div>

              {/* Payment fields grid */}
              <div className="pay-fields-grid">
                <div className="pay-field-group">
                  <label className="pay-field-label">Payment Amount (USD) *</label>
                  <input
                    type="number" min="0" step="0.01"
                    className="pay-field-input"
                    style={exceedsBalance ? { borderColor: "#dc2626" } : undefined}
                    value={payAmount}
                    onChange={(e) => {
                      const v = e.target.value;
                      this.setState({ payAmount: v === "" || Number(v) >= 0 ? v : "" });
                    }}
                    disabled={paySubmitting}
                  />
                  {exceedsBalance ? (
                    <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4, fontWeight: 600 }}>
                      Exceeds balance due of {fmtCurrency(payBalanceDue, d)}
                    </div>
                  ) : (
                    <div className="pay-field-hint">
                      {payApplyCredit && creditToApply > 0
                        ? `Cash / wire portion after ${fmtCurrency(creditToApply, d)} credit applied.`
                        : "Cash / wire portion of the payment."}
                    </div>
                  )}
                </div>
                <div className="pay-field-group">
                  <label className="pay-field-label">Payment Date *</label>
                  <input
                    type="date"
                    className="pay-field-input"
                    value={payDate}
                    onChange={(e) => this.setState({ payDate: e.target.value })}
                    disabled={paySubmitting}
                  />
                  <div className="pay-field-hint">Date payment received.</div>
                </div>
                <div className="pay-field-group">
                  <label className="pay-field-label">Payment Method</label>
                  <select
                    className="pay-field-input"
                    value={payMethod}
                    onChange={(e) => this.setState({ payMethod: e.target.value })}
                    disabled={paySubmitting}
                  >
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Wire Transfer">Wire Transfer</option>
                    <option value="ACH Transfer">ACH Transfer</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Check">Check</option>
                    <option value="Cash">Cash</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="pay-field-group">
                  <label className="pay-field-label">Mark Payment As</label>
                  <select
                    className="pay-field-input"
                    value={autoMarkAs}
                    onChange={() => {}}
                    disabled
                    style={{ opacity: 0.75, cursor: "not-allowed" }}
                  >
                    <option value="full">Full Payment — closes invoice</option>
                    <option value="partial">Partial Payment</option>
                  </select>
                </div>
              </div>

              {/* Settlement breakdown (shown when credit applied) */}
              {payApplyCredit && creditToApply > 0 && (
                <div className="pay-breakdown">
                  <div className="pay-breakdown-title">SETTLEMENT BREAKDOWN</div>
                  <div className="pay-breakdown-row">
                    <span>Credit applied</span>
                    <span className="pay-breakdown-green">{fmtCurrency(creditToApply, d)}</span>
                  </div>
                  <div className="pay-breakdown-row">
                    <span>Cash / wire portion</span>
                    <span>{fmtCurrency(cashPortion, d)}</span>
                  </div>
                  <div className={`pay-breakdown-row pay-breakdown-total${exceedsBalance ? " pay-breakdown-error" : ""}`}>
                    <span>Total settled</span>
                    <span className={exceedsBalance ? "pay-breakdown-red" : "pay-breakdown-green"}>
                      {fmtCurrency(totalSettled, d)}
                      {exceedsBalance && <span style={{ fontSize: 11, marginLeft: 6, color: "#DC2626" }}>exceeds balance!</span>}
                    </span>
                  </div>
                </div>
              )}

              {/* Reference */}
              <div className="pay-field-group" style={{ marginTop: 16 }}>
                <label className="pay-field-label">Payment Reference / Notes</label>
                <input
                  type="text"
                  className="pay-field-input"
                  placeholder="e.g. Wire ref #12345, Cheque #8892…"
                  value={payReference}
                  onChange={(e) => this.setState({ payReference: e.target.value })}
                  disabled={paySubmitting}
                />
              </div>

              {/* Payment history */}
              {(payHistoryLoading || payHistory.length > 0) && (
                <div className="pay-history-wrap">
                  <div className="pay-history-title">
                    <em className="icon ni ni-clock" style={{ marginRight: 5 }}></em>
                    Payment History
                  </div>
                  {payHistoryLoading ? (
                    <div style={{ color: "#9A9690", fontSize: 12, padding: "8px 0" }}>Loading history…</div>
                  ) : (
                    <table className="pay-history-table">
                      <thead>
                        <tr>
                          <th>DATE</th>
                          <th>AMOUNT</th>
                          <th>CREDIT</th>
                          <th>METHOD</th>
                          <th>STATUS</th>
                          <th>RECORDED BY</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payHistory.map((p, i) => (
                          <tr key={i}>
                            <td>{fmtDate(p.payment_date)}</td>
                            <td style={{ color: "#1A1917", fontWeight: 600 }}>{fmtCurrency(p.payment_amount, d)}</td>
                            <td style={{ color: Number(p.credit_applied) > 0 ? "#16a34a" : "#9A9690" }}>
                              {Number(p.credit_applied) > 0 ? fmtCurrency(p.credit_applied, d) : "—"}
                            </td>
                            <td style={{ color: "#6B6762" }}>{p.payment_method || "—"}</td>
                            <td>
                              <span style={{
                                fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                                background: p.payment_status_after === "Paid" ? "rgba(22,163,74,0.1)" : "rgba(245,158,11,0.12)",
                                color: p.payment_status_after === "Paid" ? "#16a34a" : "#D97706",
                              }}>
                                {p.payment_status_after || "—"}
                              </span>
                            </td>
                            <td style={{ color: "#6B6762", fontSize: 11 }}>{p.recorded_by_name || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

            </div>

            {/* Footer */}
            <div className="pay-modal-footer">
              <button className="pay-cancel-btn" onClick={this.handleClose} disabled={paySubmitting}>
                Cancel
              </button>
              <button
                className="pay-confirm-btn"
                onClick={this.submitPayment}
                disabled={paySubmitting || paySucceeded || exceedsBalance || dataLoading}
              >
                {paySubmitting
                  ? <><span className="spinner-border spinner-border-sm" style={{ marginRight: 6, width: 14, height: 14, borderWidth: 2 }}></span>Saving…</>
                  : "✓ Confirm Payment"}
              </button>
            </div>

          </div>
        </div>
      </div>
      <NotificationToast
        message={this.state.toastMsg}
        type={this.state.toastType}
        onDismiss={() => this.setState({ toastMsg: "" })}
      />
      </>
    );
  }
}

export default RecordPaymentModal;
